<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import {
	getCompetition,
	updateCompetition,
	setCompetitionStage,
	deleteVessel,
	type Competition,
	type CompetitionSettings,
} from '../../api/competitions'
import type { ApiError } from '../../api/client'
import { useAuthStore } from '../../stores/auth'
import CCompOrganizerControls from '../../components/CCompOrganizerControls.vue'
import CLoginButton from '../../components/CLoginButton.vue'
import GFormErrors from '../../components/GFormErrors.vue'

const route = useRoute()
const auth = useAuthStore()

const competition = ref<Competition | null>(null)
const errors = ref<{ msg: string; path?: string }[]>([])
const loading = ref(true)

onMounted(async () => {
	try {
		competition.value = await getCompetition(route.params.id as string)
	} finally {
		loading.value = false
	}
})

async function handleSettingsUpdated(data: CompetitionSettings) {
	errors.value = []
	try {
		competition.value = await updateCompetition(route.params.id as string, data)
	} catch (e) {
		errors.value = (e as ApiError).errors ?? [{ msg: String(e) }]
	}
}

const mySubmissionCount = computed(() =>
	competition.value?.vessels.filter(v => v.player.id === auth.user?.id).length ?? 0
)

async function handleDeleteVessel(vesselId: number, vesselName: string) {
	if (!window.confirm(`Delete "${vesselName}"? This cannot be undone.`)) return
	errors.value = []
	try {
		await deleteVessel(route.params.id as string, vesselId)
		competition.value = await getCompetition(route.params.id as string)
	} catch (e) {
		errors.value = (e as ApiError).errors ?? [{ msg: String(e) }]
	}
}

async function handleStageChanged(stage: 'accepting_submissions' | 'closed') {
	errors.value = []
	try {
		competition.value = await setCompetitionStage(route.params.id as string, stage)
	} catch (e) {
		errors.value = (e as ApiError).errors ?? [{ msg: String(e) }]
	}
}
</script>

<template>
	<main class="l-row l-stack">
		<GFormErrors :errors="errors" />

		<template v-if="competition">
			<h1>{{ competition.name }}</h1>

			<CCompOrganizerControls
				v-if="competition.isOrganizer"
				:competition="competition"
				@settings-updated="handleSettingsUpdated"
				@stage-changed="handleStageChanged"
			/>

			<p>Created by {{ competition.organizers.map(o => o.display_name).join(', ') }}</p>

			<!-- eslint-disable-next-line vue/no-v-html -->
			<div v-html="competition.descriptionHtml" />

			<template v-if="competition.part_constraints?.length || competition.min_size_x !== null || competition.max_size_x !== null || competition.min_size_y !== null || competition.max_size_y !== null || competition.min_size_z !== null || competition.max_size_z !== null || competition.required_hull_material">
				<h2>Craft Requirements</h2>
				<ul v-if="competition.part_constraints?.length">
					<li v-for="c in competition.part_constraints" :key="c.part_id">
						<template v-if="c.type === 'required'">Required: {{ c.part_id }}</template>
						<template v-else-if="c.type === 'not_allowed'">Not allowed: {{ c.part_id }}</template>
						<template v-else-if="c.type === 'at_least'">At least {{ c.count }}× {{ c.part_id }}</template>
						<template v-else-if="c.type === 'at_most'">At most {{ c.count }}× {{ c.part_id }}</template>
					</li>
				</ul>
				<ul v-if="competition.min_size_x !== null || competition.max_size_x !== null || competition.min_size_y !== null || competition.max_size_y !== null || competition.min_size_z !== null || competition.max_size_z !== null">
					<li v-if="competition.min_size_x !== null || competition.max_size_x !== null">
						Width (X):
						<template v-if="competition.min_size_x !== null">min {{ competition.min_size_x }}m</template>
						<template v-if="competition.max_size_x !== null">max {{ competition.max_size_x }}m</template>
					</li>
					<li v-if="competition.min_size_y !== null || competition.max_size_y !== null">
						Height (Y):
						<template v-if="competition.min_size_y !== null">min {{ competition.min_size_y }}m</template>
						<template v-if="competition.max_size_y !== null">max {{ competition.max_size_y }}m</template>
					</li>
					<li v-if="competition.min_size_z !== null || competition.max_size_z !== null">
						Length (Z):
						<template v-if="competition.min_size_z !== null">min {{ competition.min_size_z }}m</template>
						<template v-if="competition.max_size_z !== null">max {{ competition.max_size_z }}m</template>
					</li>
				</ul>
				<p v-if="competition.required_hull_material">
					Required hull material: <strong>{{ competition.required_hull_material }}</strong>
				</p>
			</template>

			<h2>
				Entries
				<template v-if="competition.vessels.length">({{ competition.vessels.length }} vessels)</template>
			</h2>

			<p>Each player may submit up to {{ competition.max_submissions }} vessel{{ competition.max_submissions !== 1 ? 's' : '' }}.</p>

			<ul>
				<li v-for="vessel in competition.vessels" :key="vessel.id">
					<strong>{{ vessel.player.display_name }}</strong> — {{ vessel.name }}
					<em v-if="vessel.player.id === auth.user?.id">
						(This is your submission. The craft file is {{ vessel.craft_file }})
					</em>
					<button
						v-if="vessel.player.id === auth.user?.id && competition.status !== 'closed'"
						@click="handleDeleteVessel(vessel.id, vessel.name)"
					>
						Delete
					</button>
				</li>
			</ul>

			<div v-if="auth.user">
				<RouterLink
					v-if="competition.status === 'accepting_submissions' && mySubmissionCount < competition.max_submissions"
					:to="`/competitions/${competition.id}/submit-vessel`"
					class="c-button"
				>
					Submit a Vessel
				</RouterLink>
			</div>
			<div v-else>
				<p>Want to join this competition? Sign in to your account with Discord!</p>

				<c-login-button/>
			</div>

		</template>

		<p v-else-if="!loading">Competition not found.</p>
	</main>
</template>
